import "./styles/layers.css";
import "@mantine/core/styles.layer.css";
import "@xplatform/design-system/styles.layer.css";
import i18n, { I18N_NAMESPACE } from "@config/i18n";
import "./index.scss";
import type React from "react";
import { Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { LoadingScreen, XPlatformProvider } from "@xplatform/design-system";
import { injectStylesheet } from "@utils/injectStyles";
import { ModuleContextProvider } from "@src/ContextProvider";
import { TranslatePage } from "@pages/TranslatePage";

/**
 * Root component exposed via Module Federation as `./App`. The shell mounts
 * this under its own `BrowserRouter`; when run standalone (local dev)
 * `main.tsx` wraps it in a router.
 *
 * Routes:
 *   `/`            redirect to `/translate`
 *   `/translate`   text + batch translation
 */
const ModuleApp: React.FC = () => {
  // The imports above only reach the DOM when this module is served
  // standalone; under federation the emitted assets/style.css is never
  // linked, so inject it from this module's own base URL.
  useEffect(() => {
    const base = import.meta.env.BASE_URL || "/";
    injectStylesheet(`${base}assets/style.css`);
  }, []);

  return (
    // Scopes every bare useTranslation() in this module to the `translate`
    // namespace, so module keys cannot collide with the shell's or another
    // module's in the shared `translation` namespace.
    <I18nextProvider i18n={i18n} defaultNS={I18N_NAMESPACE}>
      {/* The module provides its own XPlatformProvider so its Mantine and
          design-system components always resolve a MantineProvider from the
          SAME @mantine/core instance they are bundled with, independent of the
          federation host's shared scope. Nested harmlessly under the shell's. */}
      <XPlatformProvider>
        <ModuleContextProvider>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route index element={<Navigate to="translate" replace />} />
              <Route path="translate" element={<TranslatePage />} />
              <Route path="*" element={<Navigate to="translate" replace />} />
            </Routes>
          </Suspense>
        </ModuleContextProvider>
      </XPlatformProvider>
    </I18nextProvider>
  );
};

export default ModuleApp;
